// JOBE-0106: useProjectDetail — fetch the project's detail payload
// (`GET /api/projects/{urn}?expand=rollups`) and members payload
// (`GET /api/projects/{urn}/members`) in parallel.
//
// Mirrors the LibraryDocumentDetail fetch posture: useEffect +
// AbortController + `credentials: 'same-origin'`, no extra deps.
//
// Returns: { project, members, loadState, error, retry }
//   - loadState:
//       'loading'      — neither fetch resolved
//       'ready'        — both resolved successfully
//       'cedar-denied' — detail returned 404 (Cedar silent-deny per
//                        JOBE-0067.6). Members fetch is short-circuited.
//       'error'        — non-404 failure on either
//
// Spec: specs/in-progress/JOBE-0106-projects-home-route.md §"Data
// fetching seam" + §"Cedar posture".

import { useEffect, useState, useCallback } from 'react';

export function useProjectDetail(projectURN) {
  const [project, setProject] = useState(null);
  const [members, setMembers] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const retry = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!projectURN) return undefined;
    const ctrl = new AbortController();
    let alive = true;

    setProject(null);
    setMembers(null);
    setLoadState('loading');
    setError(null);

    const detailURL = `/api/projects/${encodeURIComponent(projectURN)}?expand=rollups`;
    const membersURL = `/api/projects/${encodeURIComponent(projectURN)}/members`;

    fetch(detailURL, { credentials: 'same-origin', signal: ctrl.signal })
      .then(async (res) => {
        if (!alive) return;
        if (res.status === 404) {
          // Cedar silent-deny posture — JOBE-0067.6.
          setLoadState('cedar-denied');
          return;
        }
        if (!res.ok) {
          setError({ status: res.status, where: 'detail' });
          setLoadState('error');
          return;
        }
        const body = await res.json();
        if (!alive) return;
        setProject(body || null);
        // Detail resolved happily — kick off members fetch in parallel.
        fetch(membersURL, { credentials: 'same-origin', signal: ctrl.signal })
          .then(async (mres) => {
            if (!alive) return;
            if (!mres.ok) {
              // Members failure is not page-fatal — render with empty
              // members payload and let the People lane fall back to an
              // empty state. Mirrors the silent-drop posture of ADR-0015.
              setMembers({ creator: null, assignee: null, watchers: [] });
              setLoadState('ready');
              return;
            }
            const mbody = await mres.json();
            if (!alive) return;
            setMembers(mbody || { creator: null, assignee: null, watchers: [] });
            setLoadState('ready');
          })
          .catch((err) => {
            if (!alive || err?.name === 'AbortError') return;
            setMembers({ creator: null, assignee: null, watchers: [] });
            setLoadState('ready');
          });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setError({ status: 0, where: 'detail', message: err?.message || String(err) });
        setLoadState('error');
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [projectURN, tick]);

  return { project, members, loadState, error, retry };
}
